import {
  ADMIN_PERMISSION_ACCESS_LEVEL,
  ADMIN_PERMISSION_RESOURCE_TYPE,
  GROUP_LIST_ADMIN_BULK_DELETE_ACTION,
  type GroupListAdminGroupPayloadItem,
  type GroupListAdminListPayloadItem,
  type GroupListAdminListView,
  type GroupListAdminListingView,
  type UpsertGroupListAdminPayload,
} from '@/types';
import type { GroupAdminAccessLevel } from './groupAdminPermissions';

type BuildGroupListAdminGroupsPayloadParams = {
  groups: { id: string; accessLevel: GroupAdminAccessLevel }[];
};

type BuildGroupListAdminListsPayloadParams = {
  lists: { id: string; accessLevel: GroupAdminAccessLevel }[];
};

type BuildUpsertGroupListAdminPayloadParams = {
  firstName: string;
  lastName: string;
  email: string;
  middleName?: string | null;
  phone?: string | null;
  dob?: string | null;
  selectedGroupIds: string[];
  groupAccessLevels: Record<string, GroupAdminAccessLevel>;
  selectedListIds: string[];
  listAccessLevels: Record<string, GroupAdminAccessLevel>;
};

export const DEFAULT_LIST_ADMIN_ACCESS_LEVEL: GroupAdminAccessLevel = ADMIN_PERMISSION_ACCESS_LEVEL.EDIT;

export const getListAdminAccessLevel = (list: GroupListAdminListView): GroupAdminAccessLevel => {
  if (list.accessLevel === ADMIN_PERMISSION_ACCESS_LEVEL.VIEW_ONLY) {
    return ADMIN_PERMISSION_ACCESS_LEVEL.VIEW_ONLY;
  }

  return ADMIN_PERMISSION_ACCESS_LEVEL.EDIT;
};

export const buildGroupListAdminGroupsPayload = ({
  groups,
}: BuildGroupListAdminGroupsPayloadParams): GroupListAdminGroupPayloadItem[] =>
  groups.map(({ id, accessLevel }) => ({
    id,
    permissions: [
      {
        resourceType: ADMIN_PERMISSION_RESOURCE_TYPE.MEMBERS,
        accessLevel,
      },
      {
        resourceType: ADMIN_PERMISSION_RESOURCE_TYPE.TEXT_CREDIT,
        accessLevel: ADMIN_PERMISSION_ACCESS_LEVEL.DENY,
      },
    ],
  }));

export const buildGroupListAdminListsPayload = ({
  lists,
}: BuildGroupListAdminListsPayloadParams): GroupListAdminListPayloadItem[] =>
  lists.map(({ id, accessLevel }) => ({
    id,
    accessLevel,
  }));

export const buildUpsertGroupListAdminPayload = ({
  firstName,
  lastName,
  email,
  middleName,
  phone,
  dob,
  selectedGroupIds,
  groupAccessLevels,
  selectedListIds,
  listAccessLevels,
}: BuildUpsertGroupListAdminPayloadParams): UpsertGroupListAdminPayload => ({
  firstName,
  lastName,
  email,
  middleName: middleName ?? null,
  phone: phone ?? null,
  dob: dob ?? null,
  groups: buildGroupListAdminGroupsPayload({
    groups: selectedGroupIds.map((groupId) => ({
      id: groupId,
      accessLevel: groupAccessLevels[groupId] ?? ADMIN_PERMISSION_ACCESS_LEVEL.EDIT,
    })),
  }),
  lists: buildGroupListAdminListsPayload({
    lists: selectedListIds.map((listId) => ({
      id: listId,
      accessLevel: listAccessLevels[listId] ?? DEFAULT_LIST_ADMIN_ACCESS_LEVEL,
    })),
  }),
});

export const getGroupListAdminBulkDeleteActions = (
  admin: Pick<GroupListAdminListingView, 'groups' | 'lists'>
): GROUP_LIST_ADMIN_BULK_DELETE_ACTION[] => {
  const actions: GROUP_LIST_ADMIN_BULK_DELETE_ACTION[] = [];

  if (admin.groups.length > 0) {
    actions.push(GROUP_LIST_ADMIN_BULK_DELETE_ACTION.GROUP_ACCESS);
  }

  if (admin.lists.length > 0) {
    actions.push(GROUP_LIST_ADMIN_BULK_DELETE_ACTION.LIST_ACCESS);
  }

  return actions;
};
